import dayjs from "dayjs";
import { clearLocalStorage } from "../hooks/useLocalStorage";
import { httpDelete, httpPost, httpGet, httpPut } from "./AbstractService";
import ENDPOINTS from "../data/endpoints.json";
import { jwtDecode } from "jwt-decode";
import { getKy } from "../services/AbstractService";

export function validateGoogleStudentId(school, jwt, studentId) {
  return httpPost(jwt, ENDPOINTS[school.region].user + "/user/validate/gsid", {
    schoolEid: school.eid,
    jwt,
    studentId,
  });
}

// use school just to pull a URL
export function validateGoogle(school, jwt) {
  return httpPost(jwt, ENDPOINTS[school.region].user + "/user/validate/g", {
    schoolEid: school.eid,
    jwt,
  });
}

export function reValidateGoogle(school, googleJwt, schoolEid) {
  return httpPost(
    googleJwt,
    ENDPOINTS[school.region].user + "/user/validate/rg",
    {
      schoolEid: schoolEid,
      jwt: googleJwt,
    }
  );
}

export function validateMS(school, jwt) {
  return httpPost(jwt, ENDPOINTS[school.region].user + "/user/validate/m", {
    schoolEid: school.eid,
    jwt,
  });
}

export function getPriorityText(priority) {
  switch (priority) {
    case 500:
      return "Student";
    case 600:
      return "Teacher";
    case 700:
      return "Dept Head";
    case 775:
      return "Lite Admin";
    case 800:
      return "Admin";

    default:
      return "UNK";
  }
}

export function getUserPriority(token) {
  if (token == null) return 0;

  const jsonStr = atob(token.split(".")[1]);
  if (jsonStr == null) return 0;

  const json = JSON.parse(jsonStr);
  if (json == null) return 0;

  if (json.priority == null) return 0;
  // console.log(json);

  return json.priority;
}

export function getUserEid(token) {
  if (token == null) return null;

  const jsonStr = atob(token.split(".")[1]);
  if (jsonStr == null) return null;

  const json = JSON.parse(jsonStr);
  if (json == null) return null;

  if (json.priority == null) return null;
  return json.user_eid;
}

export function isTeacher(token) {
  // console.log(getUserPriority(token));
  return getUserPriority(token) >= 600 && getUserPriority(token) <= 700;
}

export function isFaculty(token) {
  return getUserPriority(token) >= 600;
}

export function isAdmin(token) {
  return getUserPriority(token) === 800;
}

export const updateDetails = (school, jwt, values) => {
  return httpPost(
    jwt,
    ENDPOINTS[school.region].user + "/user/device/detail",
    values
  );
};

export const logoutUser = () => {
  clearLocalStorage();
};

/**
 * returns the dates, and the number of activities signed up per date
 * @param {*} school
 * @param {*} jwt
 * @returns
 */
export const getCalendarStatus = (school, jwt, q = null) => {
  let url = ENDPOINTS[school.region].user + "/user/calendar/status";

  if (q != null) {
    url += "?q=" + q;
  }

  return httpGet(jwt, url);
};

export const getStudentRecommendations = (school, jwt) => {
  return httpGet(
    jwt,
    ENDPOINTS[school.region].user + "/student/recommendation"
  );
};

export const getTeacherUserLists = (school, jwt) => {
  return httpGet(jwt, ENDPOINTS[school.region].user + "/teacher/user-list");
};

export const getUserListMembers = (school, jwt, id) => {
  return httpGet(
    jwt,
    ENDPOINTS[school.region].user + `/user-list/${id}/member`
  );
};

export const getUserListMembers_v2 = (school, jwt, eid) => {
  return httpGet(
    jwt,
    ENDPOINTS[school.region].user + `/v2/user-list/${eid}/member`
  );
};

export const addUserListMembers = (school, jwt, eid, userEntries) => {
  return httpPut(
    jwt,
    ENDPOINTS[school.region].user + `/v2/user-list/${eid}/member?type=eid`,
    {
      userEntries,
    }
  );
};

export const addUserListMembers__import = (school, jwt, eid, userEntries) => {
  return httpPut(
    jwt,
    ENDPOINTS[school.region].user + `/v2/user-list/${eid}/member?type=import`,
    {
      userEntries,
    }
  );
};

export const getSchoolStudents = (school, jwt, letter = null) => {
  let url = ENDPOINTS[school.region].user + "/user/student";

  if (letter != null) {
    url += "?letter=" + letter;
  }

  return httpGet(jwt, url);
};

export const findSchoolUsers = (school, jwt) => {
  let url = ENDPOINTS[school.region].user + "/user/5wYYGKr8krqYLFCxCH6F";
  return httpGet(jwt, url);
};

export const deleteUserList = (school, jwt, id) => {
  return httpDelete(jwt, ENDPOINTS[school.region].user + `/user-list/${id}`);
};

export const updateUserList = (school, jwt, id, name) => {
  return httpPut(
    jwt,
    ENDPOINTS[school.region].school + `/school/user-list/${id}`,
    {
      name,
    }
  );
};

export const shareUserList = (school, jwt, eid, eids) => {
  return httpPut(
    jwt,
    ENDPOINTS[school.region].user + `/v2/user-list/${eid}/share`,
    { user_eids: eids }
  );
};

export const createUserList = (school, jwt, name, description, isGlobal) => {
  return httpPost(jwt, ENDPOINTS[school.region].user + `/user-list`, {
    name,
    description,
    isGlobal,
  });
};

export const getUserList = (school, jwt, eid) => {
  return httpGet(jwt, ENDPOINTS[school.region].school + `/v2/user-list/${eid}`);
};

export const removeUserListStudent = (school, jwt, eid, memberEid) => {
  return httpDelete(
    jwt,
    ENDPOINTS[school.region].user + `/v2/user-list/${eid}/member/${memberEid}`
  );
};

export const getDepartments = (school, jwt) => {
  return httpGet(jwt, ENDPOINTS[school.region].user + `/dept/all`);
};

export const getDepartmentTeachers = (school, jwt, deptEid) => {
  return httpGet(
    jwt,
    ENDPOINTS[school.region].school + `/school/teacher?deptEid=${deptEid}`
  );
};

export const getStudentDetails = (school, jwt, eid) => {
  return httpGet(
    jwt,
    ENDPOINTS[school.region].user + `/student/${eid}/details`
  );
};

export const getUserLists = (school, jwt, eid) => {
  return httpGet(jwt, ENDPOINTS[school.region].school + `/school/user-list`);
};

export const sendFcm = (school, jwt, platform, fcm) => {
  return httpPost(jwt, ENDPOINTS[school.region].user + `/52bLXlVUNf4CV9GH`, {
    t7J0aXVxsTXf6yuC: fcm,
    platform,
  });
};

export const checkinLogin = (school, studentId) => {
  return httpPost("", ENDPOINTS[school.region].user + `/checkin/auth`, {
    studentId,
  });
};

export const getCheckinEvents = (school, jwt, date) => {
  const url = `/student/checkin/event/${date}`;
  return httpGet(jwt, ENDPOINTS[school.region].score + url);
};

export const checkinEvent = (school, jwt, date, catId, comment) => {
  return httpPost(jwt, ENDPOINTS[school.region].score + `/student/checkin`, {
    date,
    scores: [
      {
        catId,
        scoreValue: 0,
        comment: comment,
      },
    ],
  });
};

export const getCheckinHours = (school, jwt) => {
  const url = ENDPOINTS[school.region].score + `/student/checkin/hours`;
  return httpGet(jwt, url);
};

export const getCheckinHistory = (school, jwt) => {
  const url = ENDPOINTS[school.region].score + `/student/checkin/history`;
  return httpGet(jwt, url);
};

export const getStudentComments = (school, jwt) => {
  const url = ENDPOINTS[school.region].score + `/student/comment`;
  return httpGet(jwt, url);
};

export const getUserSettings = (school, jwt) => {
  const url = ENDPOINTS[school.region].user + `/user/settings`;
  return httpGet(jwt, url);
};

export const saveUserSettings = (school, jwt, settings) => {
  const url = `${ENDPOINTS[school.region].user}/user/settings`;
  return httpPut(jwt, url, settings);
};

export const getStudentSummary = (school, jwt, userEid) => {
  const date = dayjs().format("YYYY-MM-DD");
  const url = `${
    ENDPOINTS[school.region].user
  }/student/${userEid}/${date}/summary`;
  return httpGet(jwt, url);
};

export const getTeacherSummary = (school, jwt, userEid) => {
  const date = dayjs().format("YYYY-MM-DD");
  const url = `${
    ENDPOINTS[school.region].user
  }/teacher/${userEid}/${date}/summary`;
  return httpGet(jwt, url);
}; 

export const getUserSummary = (school, jwt, eid) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].user}/user2/${eid}/summary`);
};

export const getStudentHomerooms = (school, jwt, eid) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].school}/homeroom/student`);
};

export const getStudentGuardians = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].admin}/admn2/student/${eid}/guardian`
  );
};

export const makeUserListGlobal = (school, jwt, id, isGlobal) => {
  return httpPut(
    jwt,
    ENDPOINTS[school.region].school + `/school/user-list/${id}`,
    {
      global: isGlobal,
    }
  );
};

export const getIdQr = (school, jwt, eid) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].user}/user2/${eid}/idqr`);
};

export const getUserListIdQrs = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].user}/user/user-list/${eid}/idqr`
  );
};

export const getAllIdQrs = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].user}/user2/all/idqr`);
};

export const getUserByIdQr = (school, jwt, idqr) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].user}/user/idqr/${idqr}`);
};

export const getUserRegions = (email) => {
  const url = `https://a7ui00zo5m.execute-api.us-east-2.amazonaws.com/Prod/00N6ATpAoXRh7gZWkxgKpEKK8MARepm4?x=${email}`;
  return getKy(null, {
    Authorization:
        "Bearer CJzpvLRP6XShnYxlsotBsdaf6I6VgyXNbdL5pMRQLHOp0txts4GIh0l85acX0bCh"
  }).get(url).json();
};

export const getEmailFromJwt = (token) => {
  const decoded = jwtDecode(token);
  return decoded.email;
};

export const getObjectFromGoogleJwt = (token) => {
  return jwtDecode(token);
};

export const getStudentSummaryRange = (
  school,
  jwt,
  userEid,
  dateStart,
  dateEnd
) => {
  const url = `${
    ENDPOINTS[school.region].user
  }/student/${userEid}/${dateStart}/${dateEnd}/summary`;
  return httpGet(jwt, url);
};

export const getStudentTeachers = (school, jwt, eid) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].user}/user2/${eid}/teachers`);
};

export const adminUpdateStudentTeachers = (
  school,
  jwt,
  studentEid,
  teacherEids
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].user}/user2/${studentEid}/teachers`,
    {
      teacherEids,
    }
  );
};

export const getUserByBarcode = (school, jwt, externalId) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].user}/user/barcode/${externalId}`);
};

export const updateStudentValue = (school, jwt, studentEid, key, value) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].user}/user2/${studentEid}/value`,
    {
      key,
      value,
    }
  );
};


export const savePushTokenForCurrentUser = (school, jwt, deviceId, platform, token) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].user}/user/endpoint`,
    {
      deviceId,
      platform,
      token
    }
  );
};